import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  Button,
  CircularProgress,
  IconButton,
  TextField,
  Typography,
  useMediaQuery
} from '@material-ui/core'
import {
  ArrowBack as ArrowBackIcon,
  DeleteOutlined as DeleteIcon,
  InsertDriveFileOutlined as InsertDriveFileOutlinedIcon
} from '@material-ui/icons'
import clsx from 'clsx'
import { Formik } from 'formik'
import moment from 'moment'
import { EllipseStatus, FileViewerContent, SectionLayoutDialog } from 'shared'
import { StyledSelect } from 'shared/EditableMultipleSelection/EditableMultipleSelection.style'
import { PAYMENT_TYPES, SALE_ORDER_PAYMENT_STATES, STRING_EMPTY } from 'utils/constants'
import { getPriceFormat } from 'utils/functions'
import { showSnackbarError } from 'utils/snackbar'
import * as Yup from 'yup'

import { DeletePaymentConfirmDialog } from '../DeletePaymentConfirmDialog'
import { InstallmentsDialog } from '../InstallmentsDialog'

import { useStyles } from './ViewFileReceiptDialog.style'

const PAYMENT_TYPE_LABELS = {
  [PAYMENT_TYPES.MercadoPago]: 'MERCADO PAGO',
  [PAYMENT_TYPES.BankAccount]: 'TRANSFERENCIA',
  [PAYMENT_TYPES.PaymentUrl]: 'LINK DE PAGO',
  [PAYMENT_TYPES.PaymentLocation]: 'PAGO EN EFECTIVO'
}

const MIN_PAYMENT_TYPE_LABELS = {
  [PAYMENT_TYPES.MercadoPago]: 'MP',
  [PAYMENT_TYPES.BankAccount]: 'TRANSF.',
  [PAYMENT_TYPES.PaymentUrl]: 'LINK DE PAGO',
  [PAYMENT_TYPES.PaymentLocation]: 'EFECTIVO'
}

const observerOptions = [
  {
    value: SALE_ORDER_PAYMENT_STATES.PENDING,
    label: <EllipseStatus status='yellow' title='Pendiente' />
  },
  {
    value: SALE_ORDER_PAYMENT_STATES.APPROVED,
    label: <EllipseStatus status='green' title='Aprobado' />
  },
  {
    value: SALE_ORDER_PAYMENT_STATES.CANCELLED,
    label: <EllipseStatus status='red' title='Anulado' />
  },
  {
    value: SALE_ORDER_PAYMENT_STATES.EXPIRED,
    label: <EllipseStatus status='grey' title='Vencido' />,
    optionHidden: true
  }
]

const validationSchema = Yup.object().shape({
  paymentComment: Yup.string()
    .nullable()
    .max(150, 'El comentario tiene un máximo de 150 caracteres')
})

const DIALOG_STATES = {
  VIEW_RECEIPT: 'VIEW_RECEIPT',
  VIEW_RECEIPT_LIST: 'VIEW_RECEIPT_LIST'
}

const ViewFileReceiptDialog = ({
  disabledViewReceipt,
  reviewed,
  load = () => {},
  inscription,
  onUpdatePayment = () => {},
  onDeletePayment,
  currentPage,
  hasInstallments,
  payments
}) => {
  const classes = useStyles()

  const isDesktop = useMediaQuery((theme) => theme.breakpoints.up('md'))

  const { event } = useSelector((state) => state.events)

  const [openViewerRecieptDialog, setOpenViewerRecieptDialog] = useState(false)
  const [deletePaymentPending, setDeletePaymentPending] = useState(false)
  const [openDeletePaymentConfirmDialog, setOpenDeletePaymentConfirmDialog] = useState(false)
  const [receiptReviewed, setReceiptReviewed] = useState(reviewed)
  const [openInstallmentsDialog, setOpenInstallmentsDialog] = useState(false)
  const [dialogState, setDialogState] = useState(
    hasInstallments ? DIALOG_STATES.VIEW_RECEIPT_LIST : DIALOG_STATES.VIEW_RECEIPT
  )
  const [receiptSelected, setReceiptSelected] = useState(
    !hasInstallments && !disabledViewReceipt ? payments[0] : {}
  )

  const {
    firstName,
    lastName,
    idNumber,
    distance,
    category,
    saleOrder,
    ticketTypeId,
    paymentType
  } = inscription ?? {}

  useEffect(() => {
    if (openViewerRecieptDialog && !receiptReviewed) handleUpdateStatePayment()
  }, [openViewerRecieptDialog])

  useEffect(() => {
    if (openViewerRecieptDialog)
      setReceiptSelected(!hasInstallments && !disabledViewReceipt ? payments[0] : {})
  }, [openViewerRecieptDialog])

  const handleDeletePayment = async () => {
    try {
      setDeletePaymentPending(true)

      await onDeletePayment(receiptSelected)

      setOpenDeletePaymentConfirmDialog(false)
      handleOnClose()
    } catch (error) {
      showSnackbarError(error)
    } finally {
      setDeletePaymentPending(false)
    }
  }

  const handleUpdateStatePayment = async () => {
    const updateStateSucess = await onUpdatePayment(receiptSelected)

    if (updateStateSucess) setReceiptReviewed(true)
  }

  const getStatusInscription = (state) => observerOptions.find((x) => x.value === state)

  const onChangeStatus = (setFieldValue) => async (options) =>
    setFieldValue('paymentState', options.value)

  const ticketItem = saleOrder?.items.find((x) => x.ticketTypeId === ticketTypeId)
  const ticketPrice = ticketItem ? getPriceFormat(ticketItem.unitPrice) : 0
  const discountItem = saleOrder?.items.find((x) => !!x.eventDiscountCode)

  const onSubmit = async ({ paymentState, saleOrder, paymentComment: description }) => {
    const updatePayment = {
      ...receiptSelected,
      state: paymentState,
      comments:
        description.trim() !== STRING_EMPTY
          ? [...receiptSelected.comments, { description }]
          : receiptSelected.comments
    }

    const updateDataSuccess = await onUpdatePayment(updatePayment)

    if (updateDataSuccess) {
      await load(currentPage, {
        ...inscription,
        saleOrder: {
          ...saleOrder,
          payments: [
            ...saleOrder.payments.filter((x) => x.id !== updateDataSuccess.id),
            { ...updateDataSuccess }
          ]
        }
      })

      if (ticketItem?.ticketTypeHasInstallments) {
        setDialogState(DIALOG_STATES.VIEW_RECEIPT_LIST)
      } else {
        handleOnClose()
      }
    }
  }

  const handleOnClose = () => {
    setOpenViewerRecieptDialog(false)
    setDialogState(hasInstallments ? DIALOG_STATES.VIEW_RECEIPT_LIST : DIALOG_STATES.VIEW_RECEIPT)
  }

  const values = {
    ...inscription,
    paymentComment: STRING_EMPTY,
    paymentState: receiptSelected?.state ?? SALE_ORDER_PAYMENT_STATES.PENDING
  }

  const paymentLabelSelected = isDesktop
    ? PAYMENT_TYPE_LABELS[paymentType]
    : MIN_PAYMENT_TYPE_LABELS[paymentType]

  const receiptFormRender = () => (
    <Formik
      initialValues={values}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
      enableReinitialize>
      {({
        values,
        handleSubmit,
        setFieldValue,
        handleBlur,
        handleChange,
        errors,
        isValid,
        touched,
        isSubmitting
      }) => (
        <form onSubmit={handleSubmit} className={classes.mainContainer}>
          <div className={classes.headerContainer}>
            <div className={classes.titleContainer}>
              <Typography color='primary' variant='h6' className={classes.receiptTitle}>
                {isDesktop ? (
                  `${firstName} ${lastName} - DNI ${idNumber}`
                ) : (
                  <>
                    {firstName} {lastName}
                    <br />
                    D.N.I {idNumber}
                  </>
                )}
              </Typography>

              {isDesktop ? (
                <Typography
                  title={`Distancia: ${distance.name} / Categoría: ${category.name}`}
                  className={classes.distanceAndCategory}>
                  Distancia: <strong>{distance.name} / </strong> Categoría:
                  <strong> {category.name}</strong>
                </Typography>
              ) : (
                <>
                  <Typography
                    title={`Distancia: ${distance.name}`}
                    className={classes.distanceAndCategory}>
                    Distancia: <strong>{distance.name} </strong>
                  </Typography>
                  <Typography
                    title={`Categoría: ${category.name}`}
                    className={classes.distanceAndCategory}>
                    Categoría: <strong> {category.name}</strong>
                  </Typography>
                </>
              )}
              <Typography title={`Ticket: ${ticketItem?.ticketTypeName} (${ticketPrice})`}>
                Ticket:&nbsp;
                <strong>
                  {ticketItem?.ticketTypeName} ({ticketPrice})
                </strong>
              </Typography>

              <Typography>
                Forma de pago:&nbsp;
                <strong>
                  {ticketItem.ticketTypeHasInstallments ? 'Pago en cuotas' : 'Pago único'}
                </strong>
              </Typography>

              <Typography title={`Modalidad de pago: ${paymentLabelSelected}`}>
                Modalidad de pago: <strong>{paymentLabelSelected}</strong>
              </Typography>

              {receiptSelected?.installmentId && (
                <Typography>
                  Cuota:&nbsp;
                  <strong>
                    N°{receiptSelected.installment.number} - (
                    {getPriceFormat(receiptSelected.amount)})
                  </strong>
                </Typography>
              )}

              <Typography
                title={
                  discountItem
                    ? `Código de descuento: ${
                        discountItem.eventDiscountCode.name
                      } (${getPriceFormat(discountItem.unitPrice)})`
                    : '-'
                }>
                {isDesktop ? 'Código de descuento' : 'Código Dto'}:&nbsp;
                <strong>
                  {discountItem
                    ? `${discountItem.eventDiscountCode.name} (${getPriceFormat(
                        discountItem.unitPrice
                      )})`
                    : '-'}
                </strong>
              </Typography>
              {discountItem &&
                ticketItem.ticketTypeHasInstallments &&
                receiptSelected?.discount > 0 && (
                  <Typography>
                    {isDesktop ? 'Descuento aplicado' : 'Dto. aplicado'}:&nbsp;
                    <strong>{getPriceFormat(receiptSelected?.discount)}</strong>
                  </Typography>
                )}
              <Typography
                title={`Importe a pagar: ${getPriceFormat(receiptSelected?.finalAmount)}`}>
                Importe a pagar:&nbsp;
                <strong>{getPriceFormat(receiptSelected?.finalAmount)}</strong>
              </Typography>
            </div>
            <div className={classes.stateContainer}>
              <Typography color='primary' variant='h5' className={classes.stateTitle}>
                Estado
              </Typography>
              <StyledSelect
                value={getStatusInscription(values.paymentState)}
                defaultOptions={observerOptions}
                colorOptions='primary'
                filterOption={(option) =>
                  !option.data.optionHidden && values.paymentState !== option.value
                }
                isSearchable={false}
                onChange={onChangeStatus(setFieldValue)}
                className={classes.select}
              />
            </div>
          </div>

          <div className={classes.fileContainer}>
            <FileViewerContent
              fileUrl={receiptSelected?.receiptFileUrl}
              fileExtension={receiptSelected?.fileExtension}
              cropperClassName={classes.cropper}
              fileViewerClassName={classes.fileViewContainer}
            />
            {!!receiptSelected?.fileUrl && (
              <Button
                color='primary'
                variant='contained'
                className={classes.deleteButton}
                disabled={deletePaymentPending}
                onClick={() => setOpenDeletePaymentConfirmDialog(true)}>
                {!deletePaymentPending ? (
                  <DeleteIcon className={classes.deleteIcon} />
                ) : (
                  <CircularProgress color='primary' size={15} className={classes.progress} />
                )}
              </Button>
            )}
            <DeletePaymentConfirmDialog
              open={openDeletePaymentConfirmDialog}
              onDelete={handleDeletePayment}
              pending={deletePaymentPending}
              onClose={() => setOpenDeletePaymentConfirmDialog(false)}
            />
          </div>

          <div className={classes.formGroup}>
            <Typography variant='h6' color='primary'>
              Observaciones de pago
            </Typography>
            {receiptSelected.comments.map((x) => (
              <div key={x.id} style={{ display: 'flex' }}>
                <Typography className={classes.commentDescription}>
                  {x.description}&nbsp;
                  <span className={classes.commentInfo}>
                    [{moment(x.createdAt).format('DD/MM/YYYY')}, @{x.createdByUsername}]
                  </span>
                </Typography>
              </div>
            ))}

            <TextField
              variant='outlined'
              placeholder='Escriba las observaciones necesarias.'
              className={classes.textAreaEdit}
              color='primary'
              name='paymentComment'
              multiline
              error={touched.paymentComment && Boolean(errors.paymentComment)}
              helperText={errors.paymentComment}
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.paymentComment}
            />
          </div>

          <Button
            variant='contained'
            color='primary'
            className={classes.saveButton}
            endIcon={isSubmitting && <CircularProgress color='primary' size={16} />}
            disabled={!isValid || isSubmitting}
            type='submit'>
            Guardar
          </Button>

          {ticketItem.ticketTypeHasInstallments && (
            <IconButton color='primary' className={classes.arrowBackButton}>
              <ArrowBackIcon onClick={() => setDialogState(DIALOG_STATES.VIEW_RECEIPT_LIST)} />
            </IconButton>
          )}
        </form>
      )}
    </Formik>
  )

  const receiptListRender = () => {
    const ticket = event.activeInscriptionForm.ticketTypes.find((x) => x.id === ticketTypeId)

    return (
      <div className={classes.container}>
        <Typography color='primary' variant='h6' className={classes.receiptTitle}>
          {isDesktop ? (
            `${firstName} ${lastName} - DNI ${idNumber}`
          ) : (
            <>
              {firstName} {lastName}
              <br />
              D.N.I {idNumber}
            </>
          )}
        </Typography>
        <Typography title={`Ticket: ${ticketItem?.ticketTypeName} (${ticketPrice})`}>
          Ticket:&nbsp;
          <strong>
            {ticketItem?.ticketTypeName} ({ticketPrice})
          </strong>
        </Typography>
        <Typography title={`Plan de cuotas: ${ticket.installments.length} cuotas`}>
          Plan de cuotas:&nbsp;
          <strong>{ticket.installments.length} cuotas</strong>&nbsp;
          <Typography
            variant='caption'
            className={classes.link}
            color='primary'
            onClick={() => setOpenInstallmentsDialog(true)}>
            Ver plan de cuotas
          </Typography>
        </Typography>
        <InstallmentsDialog
          open={openInstallmentsDialog}
          inscription={inscription}
          onClose={() => setOpenInstallmentsDialog(false)}
        />

        <div className={classes.receiptThumbList}>
          {payments
            .filter((x) => x.installment)
            .sort((a, b) => a.installment.number - b.installment.number)
            .map((payment) => (
              <div
                key={payment.id}
                className={classes.receiptThumb}
                onClick={() => {
                  setReceiptSelected(payment)
                  setDialogState(DIALOG_STATES.VIEW_RECEIPT)
                }}>
                {payment.state === SALE_ORDER_PAYMENT_STATES.APPROVED && (
                  <Typography className={clsx(classes.banner, classes.approvedBanner)}>
                    <span className={classes.approvedText}>Aprobado</span>
                  </Typography>
                )}
                {payment.state === SALE_ORDER_PAYMENT_STATES.PENDING && (
                  <Typography className={clsx(classes.banner, classes.pendingBanner)}>
                    <span className={classes.pendingText}>Pendiente</span>
                  </Typography>
                )}
                {payment.state === SALE_ORDER_PAYMENT_STATES.CANCELLED && (
                  <Typography className={clsx(classes.banner, classes.cancelledBanner)}>
                    <span className={classes.cancelledText}>Anulado</span>
                  </Typography>
                )}

                {payment.state === SALE_ORDER_PAYMENT_STATES.EXPIRED && (
                  <Typography className={clsx(classes.banner, classes.expiredBanner)}>
                    <span className={classes.cancelledText}>Vencido</span>
                  </Typography>
                )}

                <div className={classes.receiptThumbPreview}>
                  <FileViewerContent
                    fileUrl={payment.fileUrl}
                    cropperClassName={classes.cropperOption}
                    fileViewerClassName={classes.fileViewer}
                    fileExtension={payment.fileExtension ?? STRING_EMPTY}
                    isPreview
                    zoomDisabled
                  />
                </div>
                {payment.installmentId && (
                  <div className={classes.receiptThumbFooter}>
                    <Typography variant='caption' color='primary'>
                      Cuota N°{payment.installment.number}
                    </Typography>
                    <Typography variant='caption'>
                      {payment.amount === payment.discount
                        ? 'Bonificado'
                        : `${getPriceFormat(payment.finalAmount)}`}
                    </Typography>
                  </div>
                )}
              </div>
            ))}
        </div>
      </div>
    )
  }

  return (
    <div title={disabledViewReceipt && 'Esta inscripción no tiene comprobante de pago.'}>
      <Button
        color='primary'
        variant='contained'
        className={clsx(
          classes.button,
          !disabledViewReceipt && receiptReviewed && classes.receiptReviewedButton
        )}
        disabled={disabledViewReceipt}
        onClick={() => setOpenViewerRecieptDialog(true)}>
        <InsertDriveFileOutlinedIcon />
      </Button>

      <SectionLayoutDialog
        open={openViewerRecieptDialog}
        onClose={handleOnClose}
        fullScreen={dialogState === DIALOG_STATES.VIEW_RECEIPT}
        title={
          dialogState === DIALOG_STATES.VIEW_RECEIPT
            ? 'COMPROBANTE DE PAGO'
            : 'LISTA DE COMPROBANTES'
        }
        className={clsx(dialogState === DIALOG_STATES.VIEW_RECEIPT && classes.dialog)}>
        {dialogState === DIALOG_STATES.VIEW_RECEIPT ? receiptFormRender() : receiptListRender()}
      </SectionLayoutDialog>
    </div>
  )
}

export default ViewFileReceiptDialog
